import React, { useEffect, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { DashboardLayout } from "@/components/layout/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import {
  Plus,
  Pencil,
  Trash2,
  Scissors,
  Loader2,
  FolderOpen,
  Eye,
  Layers,
} from "lucide-react";

// Quick-pick helpers
const DURATION_PRESETS = [15, 30, 45, 60, 90, 120];
const PRICE_PRESETS = [0, 10, 20, 30, 50, 75, 100];

const ServicesPage = () => {
  const { api, business } = useAuth();
  const [services, setServices] = useState([]);
  const [categories, setCategories] = useState([]);
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [categoryDialogOpen, setCategoryDialogOpen] = useState(false);
  const [editingService, setEditingService] = useState(null);
  const [saving, setSaving] = useState(false);

  // Global business setting: show service descriptions on the public booking page
  const [showDescriptionsOnPublic, setShowDescriptionsOnPublic] =
    useState(false);
  const [descToggleSaving, setDescToggleSaving] = useState(false);

  // Global business setting: show service categories (accordion) on the public booking page
  const [showCategoriesOnPublic, setShowCategoriesOnPublic] = useState(false);
  const [categoriesToggleSaving, setCategoriesToggleSaving] = useState(false);

  // duration and price are kept as strings so the user can freely clear them.
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    duration: "60",
    price: "0",
    category_id: "",
    assigned_staff_ids: [],
  });

  // Per-field validation error messages
  const [errors, setErrors] = useState({});

  const [categoryForm, setCategoryForm] = useState({
    name: "",
    description: "",
    color: "#3F6212",
  });

  useEffect(() => {
    if (business?.id) {
      fetchData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [business]);

  const fetchData = async () => {
    try {
      const [servicesRes, categoriesRes, staffRes, businessRes] =
        await Promise.all([
          api.get(`/businesses/${business.id}/services`),
          api.get(`/businesses/${business.id}/categories`),
          api.get(`/businesses/${business.id}/staff`),
          api.get(`/businesses/my`).catch(() => ({ data: {} })),
        ]);
      setServices(servicesRes.data);
      setCategories(categoriesRes.data);
      setStaff(staffRes.data);
      setShowDescriptionsOnPublic(
        Boolean(businessRes.data?.show_descriptions_on_public),
      );
      setShowCategoriesOnPublic(
        Boolean(businessRes.data?.show_categories_on_public),
      );
    } catch (error) {
      console.error("Failed to fetch data:", error);
      toast.error("Failed to load services");
    } finally {
      setLoading(false);
    }
  };

  const handleToggleDescriptions = async (next) => {
    const previous = showDescriptionsOnPublic;
    setShowDescriptionsOnPublic(next); // optimistic
    setDescToggleSaving(true);
    try {
      await api.put(`/businesses/${business.id}`, {
        show_descriptions_on_public: next,
      });
      toast.success(
        next
          ? "Descriptions are now visible on your public booking page"
          : "Descriptions are now hidden on your public booking page",
      );
    } catch (error) {
      console.error("Failed to update setting:", error);
      setShowDescriptionsOnPublic(previous); // rollback
      toast.error(
        error.response?.data?.detail ||
          "Failed to update setting. Please try again.",
      );
    } finally {
      setDescToggleSaving(false);
    }
  };

  const handleToggleCategories = async (next) => {
    const previous = showCategoriesOnPublic;
    setShowCategoriesOnPublic(next); // optimistic
    setCategoriesToggleSaving(true);
    try {
      await api.put(`/businesses/${business.id}`, {
        show_categories_on_public: next,
      });
      toast.success(
        next
          ? "Categories are now visible on your public booking page"
          : "Categories are now hidden on your public booking page",
      );
    } catch (error) {
      console.error("Failed to update setting:", error);
      setShowCategoriesOnPublic(previous); // rollback
      toast.error(
        error.response?.data?.detail ||
          "Failed to update setting. Please try again.",
      );
    } finally {
      setCategoriesToggleSaving(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: "",
      description: "",
      duration: "60",
      price: "0",
      category_id: "",
      assigned_staff_ids: [],
    });
    setErrors({});
    setEditingService(null);
  };

  const openEditDialog = (service) => {
    setEditingService(service);
    setFormData({
      name: service.name,
      description: service.description || "",
      duration: String(service.duration),
      price: String(service.price),
      category_id: service.category_id || "",
      assigned_staff_ids: service.assigned_staff_ids || [],
    });
    setErrors({});
    setDialogOpen(true);
  };

  const validateAndBuildPayload = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = "Service name is required";
    }

    const durationNum = parseInt(formData.duration, 10);
    if (
      formData.duration.trim() === "" ||
      isNaN(durationNum) ||
      durationNum <= 0
    ) {
      newErrors.duration = "Duration is required and must be a positive number";
    } else if (durationNum % 15 !== 0) {
      newErrors.duration =
        "Duration must be a multiple of 15 minutes (e.g. 15, 30, 45, 60)";
    }

    const priceNum = parseFloat(formData.price);
    if (formData.price.trim() === "" || isNaN(priceNum) || priceNum < 0) {
      newErrors.price = "Price is required and must be 0 or greater";
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      toast.error(Object.values(newErrors)[0]);
      return null;
    }

    return {
      name: formData.name.trim(),
      description: formData.description,
      duration: durationNum,
      price: priceNum,
      category_id: formData.category_id,
      assigned_staff_ids: formData.assigned_staff_ids,
    };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const payload = validateAndBuildPayload();
    if (!payload) return;

    setSaving(true);
    try {
      if (editingService) {
        await api.put(
          `/businesses/${business.id}/services/${editingService.id}`,
          payload,
        );
        toast.success("Service updated");
      } else {
        await api.post(`/businesses/${business.id}/services`, payload);
        toast.success("Service created");
      }
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      console.error("Failed to save service:", error);
      toast.error(error.response?.data?.detail || "Failed to save service");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (serviceId) => {
    if (!window.confirm("Are you sure you want to delete this service?"))
      return;

    try {
      await api.delete(`/businesses/${business.id}/services/${serviceId}`);
      toast.success("Service deleted");
      fetchData();
    } catch (error) {
      console.error("Failed to delete service:", error);
      toast.error("Failed to delete service");
    }
  };

  const handleCreateCategory = async (e) => {
    e.preventDefault();

    if (!categoryForm.name.trim()) {
      toast.error("Category name is required");
      return;
    }

    try {
      await api.post(`/businesses/${business.id}/categories`, categoryForm);
      toast.success("Category created");
      setCategoryDialogOpen(false);
      setCategoryForm({ name: "", description: "", color: "#3F6212" });
      fetchData();
    } catch (error) {
      console.error("Failed to create category:", error);
      toast.error("Failed to create category");
    }
  };

  const handleDeleteCategory = async (categoryId) => {
    if (
      !window.confirm(
        "Delete this category? Services in this category will become uncategorized.",
      )
    )
      return;

    try {
      await api.delete(`/businesses/${business.id}/categories/${categoryId}`);
      toast.success("Category deleted");
      fetchData();
    } catch (error) {
      console.error("Failed to delete category:", error);
      toast.error("Failed to delete category");
    }
  };

  const toggleStaffAssignment = (staffId) => {
    setFormData((prev) => ({
      ...prev,
      assigned_staff_ids: prev.assigned_staff_ids.includes(staffId)
        ? prev.assigned_staff_ids.filter((id) => id !== staffId)
        : [...prev.assigned_staff_ids, staffId],
    }));
  };

  const getCategoryName = (categoryId) => {
    const category = categories.find((c) => c.id === categoryId);
    return category?.name || "Uncategorized";
  };

  const groupedServices = services.reduce((acc, service) => {
    const categoryId = service.category_id || "uncategorized";
    if (!acc[categoryId]) {
      acc[categoryId] = [];
    }
    acc[categoryId].push(service);
    return acc;
  }, {});

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-pulse text-muted-foreground">
            Loading services...
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="services-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
          <div className="min-w-0">
            <h1 className="font-heading text-3xl font-extrabold">Services</h1>
            <p className="text-muted-foreground">
              Manage the services you offer to customers.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {/* Global toggle: show descriptions on public booking page */}
            <div
              className="flex items-center gap-3 rounded-xl border border-border bg-card px-3 py-2 shadow-sm"
              data-testid="show-descriptions-toggle-container"
            >
              <div className="flex items-center gap-2">
                <Eye className="w-4 h-4 text-muted-foreground" />
                <div className="flex flex-col leading-tight">
                  <Label
                    htmlFor="show-descriptions-toggle"
                    className="text-sm font-semibold cursor-pointer"
                  >
                    Show descriptions on public page
                  </Label>
                  <span className="text-[11px] text-muted-foreground">
                    Applies to all services on your booking page
                  </span>
                </div>
              </div>
              <Switch
                id="show-descriptions-toggle"
                checked={showDescriptionsOnPublic}
                disabled={descToggleSaving}
                onCheckedChange={handleToggleDescriptions}
                data-testid="show-descriptions-toggle"
              />
            </div>
            {/* Global toggle: group services by category on public booking page */}
            <div
              className="flex items-center gap-3 rounded-xl border border-border bg-card px-3 py-2 shadow-sm"
              data-testid="show-categories-toggle-container"
            >
              <div className="flex items-center gap-2">
                <Layers className="w-4 h-4 text-muted-foreground" />
                <div className="flex flex-col leading-tight">
                  <Label
                    htmlFor="show-categories-toggle"
                    className="text-sm font-semibold cursor-pointer"
                  >
                    Display service categories on booking page
                  </Label>
                  <span className="text-[11px] text-muted-foreground">
                    Group services into collapsible category sections
                  </span>
                </div>
              </div>
              <Switch
                id="show-categories-toggle"
                checked={showCategoriesOnPublic}
                disabled={categoriesToggleSaving}
                onCheckedChange={handleToggleCategories}
                data-testid="show-categories-toggle"
              />
            </div>
            {/* Add Category dialog */}
            <Dialog
              open={categoryDialogOpen}
              onOpenChange={setCategoryDialogOpen}
            >
              <DialogTrigger asChild>
                <Button variant="outline" data-testid="add-category-btn">
                  <FolderOpen className="w-4 h-4 mr-2" />
                  Add Category
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Create Category</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleCreateCategory} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="cat-name">Category Name</Label>
                    <Input
                      id="cat-name"
                      value={categoryForm.name}
                      onChange={(e) =>
                        setCategoryForm((prev) => ({
                          ...prev,
                          name: e.target.value,
                        }))
                      }
                      placeholder="e.g. Hair Services"
                      data-testid="category-name-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="cat-desc">Description</Label>
                    <Textarea
                      id="cat-desc"
                      value={categoryForm.description}
                      onChange={(e) =>
                        setCategoryForm((prev) => ({
                          ...prev,
                          description: e.target.value,
                        }))
                      }
                      placeholder="Optional description"
                      rows={2}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="cat-color">Color</Label>
                    <div className="flex gap-2">
                      <Input
                        id="cat-color"
                        type="color"
                        value={categoryForm.color}
                        onChange={(e) =>
                          setCategoryForm((prev) => ({
                            ...prev,
                            color: e.target.value,
                          }))
                        }
                        className="w-16 h-10 p-1"
                      />
                      <Input
                        value={categoryForm.color}
                        onChange={(e) =>
                          setCategoryForm((prev) => ({
                            ...prev,
                            color: e.target.value,
                          }))
                        }
                        className="flex-1"
                      />
                    </div>
                  </div>
                  <Button
                    type="submit"
                    className="w-full"
                    data-testid="save-category-btn"
                  >
                    Create Category
                  </Button>
                </form>
              </DialogContent>
            </Dialog>

            {/* Add / Edit Service dialog */}
            <Dialog
              open={dialogOpen}
              onOpenChange={(open) => {
                setDialogOpen(open);
                if (!open) resetForm();
              }}
            >
              <DialogTrigger asChild>
                <Button data-testid="add-service-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  Add Service
                </Button>
              </DialogTrigger>

              {/*
                On desktop: wider (max-w-2xl) so the form breathes and doesn't
                feel cramped. On mobile: full viewport width/height with an
                internal scrollable body so nothing gets cut off.
              */}
              <DialogContent
                className="
                  flex flex-col gap-0 p-0
                  w-full max-w-2xl
                  h-[100dvh] sm:h-auto sm:max-h-[90vh]
                  rounded-none sm:rounded-lg
                "
              >
                {/* Fixed header — never scrolls away */}
                <DialogHeader className="px-6 pt-6 pb-4 border-b border-border shrink-0">
                  <DialogTitle className="text-xl">
                    {editingService ? "Edit Service" : "Add New Service"}
                  </DialogTitle>
                </DialogHeader>

                {/* Scrollable form body */}
                <div className="overflow-y-auto flex-1 px-6 py-5">
                  <form onSubmit={handleSubmit} className="space-y-5">
                    {/* Service Name */}
                    <div className="space-y-1.5">
                      <Label htmlFor="name" className="text-base font-semibold">
                        Service Name <span className="text-destructive">*</span>
                      </Label>
                      <Input
                        id="name"
                        value={formData.name}
                        onChange={(e) => {
                          setFormData((prev) => ({
                            ...prev,
                            name: e.target.value,
                          }));
                          if (errors.name)
                            setErrors((prev) => ({ ...prev, name: undefined }));
                        }}
                        placeholder="e.g. Haircut"
                        className={`h-12 text-base ${errors.name ? "border-destructive focus-visible:ring-destructive" : ""}`}
                        data-testid="service-name-input"
                      />
                      {errors.name && (
                        <p className="text-sm text-destructive">
                          {errors.name}
                        </p>
                      )}
                    </div>

                    {/* Description */}
                    <div className="space-y-1.5">
                      <Label
                        htmlFor="description"
                        className="text-base font-semibold"
                      >
                        Description
                      </Label>
                      <Textarea
                        id="description"
                        value={formData.description}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            description: e.target.value,
                          }))
                        }
                        placeholder="Describe the service..."
                        rows={3}
                        className="text-base"
                      />
                    </div>

                    {/* Duration + Price side-by-side on sm+, stacked on mobile */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                      {/* Duration */}
                      <div className="space-y-1.5">
                        <Label
                          htmlFor="duration"
                          className="text-base font-semibold"
                        >
                          Duration (minutes){" "}
                          <span className="text-destructive">*</span>
                        </Label>
                        <p className="text-xs text-muted-foreground mb-2">
                          Select duration from buttons below, or type a custom
                          value (must be a multiple of 15 min).
                        </p>
                        <div className="flex flex-wrap gap-2 mb-1">
                          {DURATION_PRESETS.map((preset) => (
                            <button
                              key={preset}
                              type="button"
                              onClick={() => {
                                setFormData((prev) => ({
                                  ...prev,
                                  duration: String(preset),
                                }));
                                if (errors.duration)
                                  setErrors((prev) => ({
                                    ...prev,
                                    duration: undefined,
                                  }));
                              }}
                              className={`px-3 py-1.5 rounded-md border text-sm font-medium transition-colors
                                ${
                                  formData.duration === String(preset)
                                    ? "bg-primary text-primary-foreground border-primary"
                                    : "bg-background border-border hover:bg-secondary"
                                }`}
                            >
                              {preset} min
                            </button>
                          ))}
                        </div>
                        <Input
                          id="duration"
                          type="number"
                          inputMode="numeric"
                          min="1"
                          step="1"
                          value={formData.duration}
                          onChange={(e) => {
                            setFormData((prev) => ({
                              ...prev,
                              duration: e.target.value,
                            }));
                            if (errors.duration)
                              setErrors((prev) => ({
                                ...prev,
                                duration: undefined,
                              }));
                          }}
                          placeholder="e.g. 60"
                          className={`h-12 text-base ${errors.duration ? "border-destructive focus-visible:ring-destructive" : ""}`}
                          data-testid="service-duration-input"
                        />
                        {errors.duration && (
                          <p className="text-sm text-destructive">
                            {errors.duration}
                          </p>
                        )}
                      </div>

                      {/* Price */}
                      <div className="space-y-1.5">
                        <Label
                          htmlFor="price"
                          className="text-base font-semibold"
                        >
                          Price ($) <span className="text-destructive">*</span>
                        </Label>
                        <p className="text-xs text-muted-foreground mb-2">
                          Select price from buttons below, or enter a custom
                          amount in the field.
                        </p>
                        <div className="flex flex-wrap gap-2 mb-1">
                          {PRICE_PRESETS.map((preset) => (
                            <button
                              key={preset}
                              type="button"
                              onClick={() => {
                                setFormData((prev) => ({
                                  ...prev,
                                  price: String(preset),
                                }));
                                if (errors.price)
                                  setErrors((prev) => ({
                                    ...prev,
                                    price: undefined,
                                  }));
                              }}
                              className={`px-3 py-1.5 rounded-md border text-sm font-medium transition-colors
                                ${
                                  formData.price === String(preset)
                                    ? "bg-primary text-primary-foreground border-primary"
                                    : "bg-background border-border hover:bg-secondary"
                                }`}
                            >
                              ${preset}
                            </button>
                          ))}
                        </div>
                        <div className="relative">
                          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-base font-medium pointer-events-none select-none">
                            $
                          </span>
                          <Input
                            id="price"
                            type="number"
                            inputMode="decimal"
                            min="0"
                            step="0.01"
                            value={formData.price}
                            onChange={(e) => {
                              setFormData((prev) => ({
                                ...prev,
                                price: e.target.value,
                              }));
                              if (errors.price)
                                setErrors((prev) => ({
                                  ...prev,
                                  price: undefined,
                                }));
                            }}
                            placeholder="0.00"
                            className={`h-12 text-base pl-7 ${errors.price ? "border-destructive focus-visible:ring-destructive" : ""}`}
                            data-testid="service-price-input"
                          />
                        </div>
                        {errors.price && (
                          <p className="text-sm text-destructive">
                            {errors.price}
                          </p>
                        )}
                      </div>
                    </div>

                    {/* Category */}
                    <div className="space-y-1.5">
                      <Label
                        htmlFor="category"
                        className="text-base font-semibold"
                      >
                        Category
                      </Label>
                      <Select
                        value={formData.category_id}
                        onValueChange={(value) =>
                          setFormData((prev) => ({
                            ...prev,
                            category_id: value === "none" ? "" : value,
                          }))
                        }
                      >
                        <SelectTrigger
                          className="h-12 text-base"
                          data-testid="service-category-select"
                        >
                          <SelectValue placeholder="Select a category" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">No Category</SelectItem>
                          {categories.map((cat) => (
                            <SelectItem key={cat.id} value={cat.id}>
                              <div className="flex items-center gap-2">
                                <div
                                  className="w-3 h-3 rounded-full"
                                  style={{ backgroundColor: cat.color }}
                                />
                                {cat.name}
                              </div>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Assign Staff */}
                    {staff.length > 0 && (
                      <div className="space-y-1.5">
                        <Label className="text-base font-semibold">
                          Assign Staff
                        </Label>
                        <div className="space-y-1 max-h-40 overflow-y-auto rounded-md border border-border p-2">
                          {staff.map((s) => (
                            <label
                              key={s.id}
                              className="flex items-center gap-3 p-2.5 rounded hover:bg-secondary cursor-pointer"
                            >
                              <input
                                type="checkbox"
                                checked={formData.assigned_staff_ids.includes(
                                  s.id,
                                )}
                                onChange={() => toggleStaffAssignment(s.id)}
                                className="w-5 h-5 rounded accent-primary"
                              />
                              <span className="text-base">
                                {s.name || s.email}
                              </span>
                            </label>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Submit */}
                    <Button
                      type="submit"
                      className="w-full h-12 text-base font-semibold"
                      disabled={saving}
                      data-testid="save-service-btn"
                    >
                      {saving ? (
                        <>
                          <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                          Saving...
                        </>
                      ) : editingService ? (
                        "Update Service"
                      ) : (
                        "Create Service"
                      )}
                    </Button>
                  </form>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {/* Categories chips */}
        {categories.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {categories.map((cat) => (
              <div
                key={cat.id}
                className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-sm"
                style={{ backgroundColor: `${cat.color}20`, color: cat.color }}
              >
                <div
                  className="w-2 h-2 rounded-full"
                  style={{ backgroundColor: cat.color }}
                />
                {cat.name}
                <button
                  onClick={() => handleDeleteCategory(cat.id)}
                  className="hover:bg-white/20 rounded p-0.5"
                >
                  <Trash2 className="w-3 h-3" />
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Services list */}
        {services.length === 0 ? (
          <div className="card-base text-center py-12">
            <Scissors className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-50" />
            <h3 className="font-heading font-bold text-lg mb-2">
              No services yet
            </h3>
            <p className="text-muted-foreground mb-4">
              Add your first service to start accepting bookings.
            </p>
            <Button onClick={() => setDialogOpen(true)}>
              <Plus className="w-4 h-4 mr-2" />
              Add Service
            </Button>
          </div>
        ) : (
          <div className="space-y-6">
            {Object.entries(groupedServices).map(
              ([categoryId, categoryServices]) => (
                <div key={categoryId}>
                  <h3 className="font-heading font-bold text-lg mb-3">
                    {categoryId === "uncategorized"
                      ? "Uncategorized"
                      : getCategoryName(categoryId)}
                  </h3>
                  <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                    {categoryServices
                      .filter((s) => s.is_active !== false)
                      .map((service) => (
                        <div key={service.id} className="card-base">
                          <div className="flex items-start justify-between mb-3">
                            <div>
                              <h4 className="font-heading font-bold">
                                {service.name}
                              </h4>
                              {service.description && (
                                <p className="text-sm text-muted-foreground mt-1 line-clamp-2">
                                  {service.description}
                                </p>
                              )}
                            </div>
                            <div className="flex gap-1">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => openEditDialog(service)}
                                data-testid={`edit-service-${service.id}`}
                              >
                                <Pencil className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDelete(service.id)}
                                className="text-destructive hover:text-destructive"
                                data-testid={`delete-service-${service.id}`}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                          <div className="flex items-center justify-between text-sm">
                            <span className="text-muted-foreground">
                              {service.duration} min
                            </span>
                            <span className="font-accent font-semibold">
                              ${service.price.toFixed(2)}
                            </span>
                          </div>
                          {service.assigned_staff_ids?.length > 0 && (
                            <div className="mt-3 pt-3 border-t border-border">
                              <span className="text-xs text-muted-foreground">
                                {service.assigned_staff_ids.length} staff
                                assigned
                              </span>
                            </div>
                          )}
                        </div>
                      ))}
                  </div>
                </div>
              ),
            )}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default ServicesPage;
